import { API_BASE } from "./api";

// const API_BASE = "http://localhost:3001"; // For local development

const buildAuthHeaders = (token) =>
  token ? { Authorization: `Bearer ${token}` } : {};

const handleJsonResponse = async (res, errorContext) => {
  const text = await res.text();
  if (!res.ok) {
    throw new Error(`${errorContext} failed (${res.status}): ${text}`);
  }
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`${errorContext} returned invalid JSON: "${text}"`);
  }
};

const isMongoId = (value) => /^[a-f0-9]{24}$/i.test(String(value || ""));
const normalizeShopId = (value) => (isMongoId(value) ? value : null);

const buildOperatingCostUrl = ({ shopId, entryId }) => {
  const safeShopId = normalizeShopId(shopId);
  const base = safeShopId
    ? `${API_BASE}/shop/${shopId}/operating-costs`
    : `${API_BASE}/shop/operating-costs`;
  return entryId ? `${base}/${entryId}` : base;
};

const buildDataReadinessUrl = ({ shopId }) => {
  const safeShopId = normalizeShopId(shopId);
  return safeShopId
    ? `${API_BASE}/shop/${safeShopId}/data-readiness`
    : `${API_BASE}/shop/data-readiness`;
};

const appendUserQuery = (url, userId) =>
  userId ? `${url}?userId=${encodeURIComponent(userId)}` : url;

export async function getOperatingCosts({ shopId, userId, authToken }) {
  if (!authToken) throw new Error("authToken is required");
  const url = appendUserQuery(
    buildOperatingCostUrl({ shopId: normalizeShopId(shopId) }),
    userId,
  );
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(authToken),
    },
  });
  return handleJsonResponse(res, "Fetch operating costs");
}

export async function addOperatingCost({ shopId, userId, payload, authToken }) {
  if (!authToken) throw new Error("authToken is required");
  const url = appendUserQuery(
    buildOperatingCostUrl({ shopId: normalizeShopId(shopId) }),
    userId,
  );
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(authToken),
    },
    body: JSON.stringify(payload),
  });
  return handleJsonResponse(res, "Add operating cost");
}

export async function updateOperatingCost({
  shopId,
  entryId,
  userId,
  payload,
  authToken,
}) {
  if (!authToken) throw new Error("authToken is required");
  if (!entryId) throw new Error("entryId is required");
  const url = appendUserQuery(
    buildOperatingCostUrl({ shopId: normalizeShopId(shopId), entryId }),
    userId,
  );
  const res = await fetch(url, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(authToken),
    },
    body: JSON.stringify(payload),
  });
  return handleJsonResponse(res, "Update operating cost");
}

export async function deleteOperatingCost({
  shopId,
  entryId,
  userId,
  authToken,
}) {
  if (!authToken) throw new Error("authToken is required");
  if (!entryId) throw new Error("entryId is required");
  const url = appendUserQuery(
    buildOperatingCostUrl({ shopId: normalizeShopId(shopId), entryId }),
    userId,
  );
  const res = await fetch(url, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(authToken),
    },
  });
  return handleJsonResponse(res, "Delete operating cost");
}

export async function updateOperatingCostMain({
  shopId,
  userId,
  payload,
  authToken,
}) {
  if (!authToken) throw new Error("authToken is required");
  const base = buildOperatingCostUrl({ shopId: normalizeShopId(shopId) });
  const url = appendUserQuery(`${base}/main`, userId);
  const res = await fetch(url, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(authToken),
    },
    body: JSON.stringify(payload),
  });
  return handleJsonResponse(res, "Update main operating cost");
}

export async function getShopDataReadiness({ shopId, userId, authToken }) {
  if (!authToken) throw new Error("authToken is required");
  const url = appendUserQuery(buildDataReadinessUrl({ shopId }), userId);
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(authToken),
    },
  });
  return handleJsonResponse(res, "Fetch shop data readiness");
}

export async function validateWooCommerceConnection({
  storeUrl,
  consumerKey,
  consumerSecret,
  requireWrite = false,
}) {
  if (!storeUrl) throw new Error("storeUrl is required");
  if (!consumerKey) throw new Error("consumerKey is required");
  if (!consumerSecret) throw new Error("consumerSecret is required");

  const payload = {
    storeUrl,
    consumerKey,
    consumerSecret,
    requireWrite,
  };

  const myHeaders = new Headers();
  myHeaders.append("Content-Type", "application/json");

  const requestOptions = {
    method: "POST",
    headers: myHeaders,
    body: JSON.stringify(payload),
    redirect: "follow",
  };

  const res = await fetch(
    `${API_BASE}/integrations/woocommerce/validate`,
    requestOptions,
  );

  return handleJsonResponse(res, "Validate WooCommerce connection");
}

export async function sendOtp(email) {
  if (!email) throw new Error("email is required");

  const payload = {
    email,
  };

  const myHeaders = new Headers();
  myHeaders.append("Content-Type", "application/json");

  const requestOptions = {
    method: "POST",
    headers: myHeaders,
    body: JSON.stringify(payload),
    redirect: "follow",
  };

  const res = await fetch(`${API_BASE}/api/otp/send`, requestOptions);

  return handleJsonResponse(res, "Send OTP");
}

export async function connectWooCommerceWithOtp({
  email,
  otp,
  storeUrl,
  consumerKey,
  consumerSecret,
  requireWrite = false,
}) {
  if (!email) throw new Error("email is required");
  if (!otp) throw new Error("otp is required");
  if (!storeUrl) throw new Error("storeUrl is required");
  if (!consumerKey) throw new Error("consumerKey is required");
  if (!consumerSecret) throw new Error("consumerSecret is required");

  const payload = {
    email,
    otp,
    storeUrl,
    consumerKey,
    consumerSecret,
    requireWrite,
  };

  const myHeaders = new Headers();
  myHeaders.append("Content-Type", "application/json");

  const requestOptions = {
    method: "POST",
    headers: myHeaders,
    body: JSON.stringify(payload),
    redirect: "follow",
  };

  const res = await fetch(
    `${API_BASE}/integrations/woocommerce/connect`,
    requestOptions,
  );

  return handleJsonResponse(res, "Connect WooCommerce with OTP");
}
